<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { array, object, string } from 'yup';

const { newsArticle, newsThemes, tenantTypes } = defineProps({
    newsArticle: {
        type: Object,
        required: false,
    },
    newsThemes: {
        type: Array,
        required: true,
    },
    tenantTypes: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

function toDateTimeLocal(date) {
    return date.slice(0, 16).replace(' ', 'T');
}

function getCurrentDateTimeLocal() {
    const now = new Date();

    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
}

const form = reactive({
    newsTheme: newsArticle
        ? (newsThemes.find(
              (newsTheme) => newsTheme.value === newsArticle.newsThemeId,
          ) ?? null)
        : (newsThemes[0] ?? null),
    title: newsArticle ? newsArticle.title : '',
    content: newsArticle ? newsArticle.content : '',
    tenantTypes: newsArticle
        ? tenantTypes.filter((tenantType) =>
              newsArticle.tenantTypes.some(
                  (selected) => selected.value === tenantType.value,
              ),
          )
        : [],
    publishedAt: newsArticle
        ? toDateTimeLocal(newsArticle.publishedAt)
        : getCurrentDateTimeLocal(),
});

const validationSchema = object({
    newsTheme: object().nullable().required('Ce champ est obligatoire.'),
    title: string().required('Ce champ est obligatoire.'),
    content: string().required('Ce champ est obligatoire.'),
    tenantTypes: array(),
    publishedAt: string().required('Ce champ est obligatoire.'),
});

const recipientsLegend = computed(() =>
    form.tenantTypes.length
        ? 'Seuls les clients des types sélectionnés verront cette actualité.'
        : 'Aucun type sélectionné : cette actualité sera visible par tous les clients.',
);

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.apikom.postNewsArticle', {
                ...(newsArticle ? { newsArticle: newsArticle.id } : {}),
            }),
            {
                newsThemeId: form.newsTheme?.value,
                title: form.title,
                content: form.content,
                tenantTypes: form.tenantTypes.map(
                    (tenantType) => tenantType.value,
                ),
                publishedAt: form.publishedAt.replace('T', ' '),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(route('front.projectManagement.apikom.newsArticles'));
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="3">
                    <InputField
                        v-model="form.title"
                        label="Titre"
                        field-name="title"
                        field-id="title"
                        required
                    />

                    <ListboxField
                        v-model="form.newsTheme"
                        field-id="newsTheme"
                        field-name="newsTheme"
                        label="Thème"
                        :options="newsThemes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.newsTheme?.label }}
                        </template>

                        <template #optionContent="{ option }">
                            <span
                                class="mr-2 inline-block size-3 shrink-0 rounded-full"
                                :style="{ backgroundColor: option.color }"
                            ></span>

                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.publishedAt"
                        label="Date de publication"
                        legend="Une date future programme l'affichage de l'actualité côté client."
                        field-name="publishedAt"
                        field-id="publishedAt"
                        type="datetime-local"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <ListboxField
                        v-model="form.tenantTypes"
                        field-id="tenantTypes"
                        field-name="tenantTypes"
                        label="Destinataires"
                        :legend="recipientsLegend"
                        :options="tenantTypes"
                        multiple
                    >
                        <template #buttonContent>
                            {{
                                form.tenantTypes.length
                                    ? form.tenantTypes
                                          .map((tenantType) => tenantType.label)
                                          .join(', ')
                                    : 'Tous'
                            }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <EditorField
                        v-model="form.content"
                        label="Contenu"
                        field-name="content"
                        field-id="content"
                        media-path="news"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="newsArticle"
                    :url="
                        route(
                            'front.projectManagement.apikom.deleteNewsArticle',
                            {
                                newsArticle: newsArticle.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.newsArticles',
                                ),
                            );
                        }
                    "
                    content="Êtes-vous sûr de vouloir supprimer cette actualité ?"
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
